<template>
  <BaseDialog
    :open="open"
    :disabled="loading"
    max-width="1360px"
    @close="onClose"
  >
    <template #title>
      <span>Schüler zuweisen</span>
      <span v-if="offer">
        — <strong>{{ offer.title || ('Angebot #' + offer.id) }}</strong>
        <template v-if="offer.topic"> ({{ offer.topic }})</template>
        - {{ offer.teacher || '—' }}
      </span>
    </template>

    <div class="dialog-scroll">
      <div
        class="form"
        style="display:grid;grid-template-columns:1fr 1fr;gap:12px;"
      >
        <!-- Verfügbare Schüler (Pool) -->
        <div class="field">
          <label for="flt-students">Verfügbare Schüler</label>
          <input
            id="flt-students"
            v-model="studentFilter"
            autocomplete="off"
            placeholder="Filtern (Name/Nummer)"
          >
          <div
            class="chip-box"
            style="margin-top:8px;"
          >
            <div
              class="chips"
              style="display:flex;flex-wrap:wrap;gap:6px;max-height:300px;overflow:auto;"
            >
              <button
                v-for="s in filteredPool"
                :key="s.id"
                type="button"
                class="chip"
                :disabled="loading"
                :title="studentLabel(s)"
                @click="addStudent(s.id)"
              >
                {{ studentLabel(s) }}
              </button>
              <p
                v-if="filteredPool.length===0"
                class="muted"
              >
                Keine Treffer.
              </p>
            </div>
          </div>

          <!-- Gruppen (Sammelzuweisung) -->
          <div class="field mt-4">
            <label for="flt-groups">Gruppen (Sammelzuweisung)</label>
            <input
              id="flt-groups"
              v-model="groupFilter"
              autocomplete="off"
              placeholder="Gruppen filtern"
            >
            <div
              class="chip-box"
              style="margin-top:8px;"
            >
              <div
                class="chips"
                style="display:flex;flex-wrap:wrap;gap:6px;max-height:180px;overflow:auto;"
              >
                <button
                  v-for="g in filteredGroups"
                  :key="g.id"
                  type="button"
                  :class="['chip', g.is_private ? 'warn' : '']"
                  :disabled="loading"
                  :title="groupLabel(g)"
                  @click="addGroup(g.id)"
                >
                  {{ groupLabel(g) }}
                </button>
                <p
                  v-if="filteredGroups.length===0"
                  class="muted"
                >
                  Keine Gruppen verfügbar.
                </p>
              </div>
            </div>
            <p
              v-if="visibleGroups.length"
              class="help"
            >
              Tipp: Klick auf eine Gruppe weist alle Mitglieder dem Angebot
              zu.
            </p>
          </div>
        </div>

        <!-- Zugewiesene Schüler -->
        <div class="field">
          <label>Zugewiesene Schüler</label>
          <div
            class="chip-box"
            style="margin-top:8px;"
          >
            <div
              class="chips"
              style="display:flex;flex-wrap:wrap;gap:6px;max-height:492px;overflow:auto;"
            >
              <button
                v-for="s in assignedStudents"
                :key="s.id"
                type="button"
                class="chip active"
                :disabled="loading"
                :title="studentLabel(s)"
                @click="removeStudent(s.id)"
              >
                {{ studentLabel(s) }}
              </button>
              <p
                v-if="assignedStudents.length===0"
                class="muted"
              >
                Noch keine Schüler zugewiesen.
              </p>
            </div>
          </div>
        </div>
      </div>

      <p
        v-if="errorMsg"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ errorMsg }}
      </p>

      <div
        class="mt-12"
        style="display:flex;gap:8px;justify-content:flex-end;"
      >
        <button
          class="btn"
          type="button"
          :disabled="loading"
          @click="onClose"
        >
          Schließen
        </button>
      </div>
    </div>
  </BaseDialog>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import api from '@/services/api';

const props = defineProps({
  open: {type: Boolean, default: false},
  offer: {type: Object, default: null}, // erwartet: { id, title?, topic?, teacher? }
  mode: {type: String, default: 'event'} // 'event' | 'autoOffer'
});
const emit = defineEmits(['close', 'applied']);

const loading = ref(false);
const errorMsg = ref('');
const studentFilter = ref('');
const groupFilter = ref('');

const allStudents = ref([]);            // alle Schüler (für Anzeige)
const assignedIds = ref(new Set());     // Set<number> der eingeschriebenen Schüler im Angebot
const groups = ref([]);                 // alle Gruppen
const groupMembersMap = ref(new Map()); // Map<groupId, Set<studentId>>

// Anzeigezuordnungen
const assignedStudents = computed(() => allStudents.value.filter(s => assignedIds.value.has(s.id)));

// Nur aktive, noch nicht zugewiesene Schüler im Pool anzeigen
const poolStudents = computed(() => allStudents.value.filter(s =>
    !assignedIds.value.has(s.id) &&
    String(s?.status_code ?? '').toLowerCase() === 'aktiv'
));

const filteredPool = computed(() => {
  const q = studentFilter.value.trim().toLowerCase();
  if (!q) return poolStudents.value;
  return poolStudents.value.filter(s => studentLabel(s).toLowerCase().includes(q));
});

// Sichtbare Gruppen: nur Gruppen zeigen, bei denen mind. ein Mitglied NICHT zugewiesen ist
const visibleGroups = computed(() => {
  return groups.value.filter(g => {
    const members = groupMembersMap.value.get(Number(g.id)) || new Set();
    if (members.size === 0) return false;
    // hat die Gruppe mind. ein Mitglied, das nicht zugewiesen ist?
    for (const sid of members) {
      if (!assignedIds.value.has(sid)) return true;
    }
    return false; // alle Mitglieder sind bereits zugewiesen -> Chip ausblenden
  });
});

const filteredGroups = computed(() => {
  const q = groupFilter.value.trim().toLowerCase();
  if (!q) return visibleGroups.value;
  return visibleGroups.value.filter(g => groupLabel(g).toLowerCase().includes(q));
});

function studentLabel(s) {
  const num = s.student_number ? ` · ${s.student_number}` : '';
  return `${s.last_name}, ${s.first_name}${num}`;
}

function groupLabel(g) {
  const count = (groupMembersMap.value.get(Number(g.id)) || new Set()).size;
  const base = `${g.name}${count ? ' (' + count + ')' : ''}`;
  const priv = g.is_private ? ' - privat' : '';
  return `${base}${priv}`;
}

function getEntityId() {
  return Number(props.offer?.id ?? 0);
}

function urlListAssigned() {
  const id = getEntityId();
  return props.mode === 'autoOffer'
      ? `/auto-offers/${id}/participants`
      : `/events/${id}/enrollments`;
}

function urlAddSingle() {
  const id = getEntityId();
  return props.mode === 'autoOffer'
      ? `/auto-offers/${id}/participants`
      : `/events/${id}/enrollments`;
}

function urlRemoveSingle(studentId) {
  const id = getEntityId();
  return props.mode === 'autoOffer'
      ? `/auto-offers/${id}/participants/${Number(studentId)}`
      : `/events/${id}/enrollments/${Number(studentId)}`;
}

function urlBulk() {
  const id = getEntityId();
  return props.mode === 'autoOffer'
      ? `/auto-offers/${id}/participants/bulk`
      : `/events/${id}/enrollments/bulk`;
}

async function loadData() {
  if (!getEntityId()) return;
  loading.value = true;
  errorMsg.value = '';

  try {
    const [{data: st}, {data: en}, {data: gr}] = await Promise.all([
      api.get('/students', {params: {page: 1, limit: 1000}}),
      api.get(urlListAssigned()),
      api.get('/groups', {params: {page: 1, limit: 500, withCounts: false}})
    ]);

    allStudents.value = Array.isArray(st?.items) ? st.items : [];

    const enItems = Array.isArray(en?.items)
        ? en.items
        : (Array.isArray(en?.data?.items) ? en.data.items : []);

    const assigned = new Set(
        (enItems || []).map(s => Number(s?.id ?? s?.student_id)).filter(n => Number.isFinite(n))
    );

    // Eingeschriebene Schüler in allStudents mergen, falls nicht vorhanden
    const byId = new Map(allStudents.value.map(s => [Number(s.id), s]));
    for (const s of enItems) {
      const sid = Number(s?.id ?? s?.student_id);
      if (!Number.isFinite(sid) || byId.has(sid)) continue;
      byId.set(sid, {
        id: sid,
        first_name: s.first_name ?? s.firstName ?? '',
        last_name: s.last_name ?? s.lastName ?? '',
        student_number: s.student_number ?? s.studentNumber ?? null,
        status_code: s.status_code ?? s.statusCode ?? null
      });
    }
    allStudents.value = Array.from(byId.values());
    assignedIds.value = assigned;

    // Gruppenliste speichern
    groups.value = Array.isArray(gr?.items) ? gr.items : [];

    // 2) Mitglieder jeder Gruppe laden (für Sichtbarkeits-Logik)
    const memberPairs = await Promise.all(
        groups.value.map(async (g) => {
          try {
            const {data} = await api.get(`/groups/${g.id}/members`);
            const ids = new Set((Array.isArray(data?.items) ? data.items : []).map(s => Number(s.id)));
            return [Number(g.id), ids];
          } catch {
            return [Number(g.id), new Set()];
          }
        })
    );
    const m = new Map(memberPairs);
    groupMembersMap.value = m;
  } catch (e) {
    errorMsg.value = e?.response?.data?.error?.message || 'Laden fehlgeschlagen.';
    allStudents.value = [];
    groups.value = [];
    groupMembersMap.value = new Map();
    assignedIds.value = new Set();
  } finally {
    loading.value = false;
  }
}

watch(() => props.open, (val) => {
  if (val) {
    loadData();
  } else {
    // zurücksetzen
    allStudents.value = [];
    groups.value = [];
    groupMembersMap.value = new Map();
    assignedIds.value = new Set();
    studentFilter.value = '';
    groupFilter.value = '';
    errorMsg.value = '';
  }
});

function onClose() {
  emit('close');
}

async function addStudent(studentId) {
  if (!getEntityId()) return;
  loading.value = true;
  try {
    await api.post(urlAddSingle(), {studentId: Number(studentId)});
    const s = new Set(assignedIds.value);
    s.add(Number(studentId));
    assignedIds.value = s;
    emit('applied');
  } catch (e) {
    errorMsg.value = e?.response?.data?.error?.message || 'Zuweisen fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

async function removeStudent(studentId) {
  if (!getEntityId()) return;
  loading.value = true;
  try {
    await api.delete(urlRemoveSingle(studentId));
    const s = new Set(assignedIds.value);
    s.delete(Number(studentId));
    assignedIds.value = s;
    // Gruppensichtbarkeit aktualisiert sich reaktiv über visibleGroups
    emit('applied');
  } catch (e) {
    errorMsg.value = e?.response?.data?.error?.message || 'Entfernen fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

async function addGroup(groupId) {
  if (!getEntityId()) return;
  const gid = Number(groupId);
  const members = groupMembersMap.value.get(gid) || new Set();
  if (members.size === 0) return;

  loading.value = true;
  try {
    // Bevorzugt Bulk-Endpoint nutzen (falls vorhanden)
    try {
      await api.post(urlBulk(), {groupId: gid});
      // Lokalen Zustand aktualisieren
      const updated = new Set(assignedIds.value);
      members.forEach(id => updated.add(Number(id)));
      assignedIds.value = updated;
    } catch {
      // Fallback: Einzelzuweisungen
      const updated = new Set(assignedIds.value);
      for (const sid of members) {
        try {
          if (!updated.has(sid)) {
            await api.post(urlAddSingle(), {studentId: Number(sid)});
            updated.add(Number(sid));
          }
        } catch (e) {
          // Fehler einzelner Zuweisung: weiter versuchen, aber Fehler anzeigen
          errorMsg.value = e?.response?.data?.error?.message || 'Teilweise Zuweisung fehlgeschlagen.';
        }
      }
      assignedIds.value = updated;
    }
    emit('applied');
    // Gruppenchip blendet sich reaktiv aus, wenn alle Mitglieder zugewiesen sind
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  if (props.open) loadData();
});
</script>

<style scoped>
.chip-box {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  padding: 8px;
}

.chip {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 13px;
  line-height: 1.4;
}

.chip.active {
  background: rgba(79, 70, 229, 0.12);
  border-color: var(--primary);
  color: var(--primary-600);
}

/* Warn-Variante für private Gruppen */
.chip.warn {
  background: rgba(245, 158, 11, 0.12); /* bg-warn */
  border-color: rgba(245, 158, 11, 0.5);
  color: var(--warn);
}

.muted {
  color: var(--muted);
}

.help {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
}

.notice-warn {
  background: color-mix(in srgb, var(--warn) 12%, var(--surface));
  border-color: color-mix(in srgb, var(--warn) 40%, var(--border));
  list-style: disc;
  padding-left: 20px;
}

.notice-warn li + li {
  margin-top: 4px;
}

/* Inhalt des Dialogs auf maximal 80vh begrenzen und scrollbar machen */
.dialog-scroll {
  max-height: 80vh;
  overflow: auto;
}
</style>
